import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getGetMedicationQueryOptions } from '../../../../../api/generated/medication-controller/medication-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  MedicationDto,
  PatientAddressDtoReferenceType,
  TherapyDtoTherapyResult,
  TherapyMedicationDtoPhase,
} from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useCreateTherapyByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import {
  addDateMonths,
  formatDateForInput,
  getCurrentDate,
  getDateAfterInputValue,
  getDifferenceInDays,
  getDifferenceInDaysFromToday,
  isInputDateValue,
} from '../../../../../utils/date.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';
import { SelectMedication } from '../SelectMedication.tsx';

interface FormValues {
  startPhase1: string;
  end: string;
  therapyMedication: MedicationDto[];
  daysSinceStart: number;
  daysTotal: number;
  phase1DoctorAddress: AddressDto | null;
  phase1DoctorContact: AddressContactDto | null;
}

export function CreateTherapyDialog({
  open,
  onClose,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
}) {
  const queryClient = useQueryClient();
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields, isDirty: isFormDirty },
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      startPhase1: '',
      end: '',
      therapyMedication: [],
      phase1DoctorContact: null,
      phase1DoctorAddress: null,
      daysSinceStart: undefined,
      daysTotal: undefined,
    },
  });

  const createTherapyMutation = useCreateTherapyByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
        void queryClient.resetQueries(getGetMedicationQueryOptions());
        onClose();
      },
      onError: () => {
        toast.error('Beim Speichern der Therapie ist ein Fehler aufgetreten');
      },
    },
  });

  const startPhase1Value = watch('startPhase1');
  const endValue = watch('end');
  const endMinDate = getDateAfterInputValue(startPhase1Value);

  useEffect(() => {
    if (!startPhase1Value) return;

    if (!isInputDateValue(startPhase1Value)) {
      if (!dirtyFields.end && endValue) {
        setValue('end', '', { shouldValidate: true });
      }
      return;
    }

    setValue('daysSinceStart', getDifferenceInDaysFromToday(startPhase1Value));
    if (!dirtyFields.end) {
      const endDate = addDateMonths(6, startPhase1Value);
      if (endDate) {
        const nextEndValue = formatDateForInput(endDate);
        if (endValue !== nextEndValue) {
          setValue('end', nextEndValue, { shouldValidate: true });
        }
      }
    }
  }, [dirtyFields.end, endValue, setValue, startPhase1Value]);

  useEffect(() => {
    if (startPhase1Value && endValue) {
      setValue('daysTotal', getDifferenceInDays(startPhase1Value, endValue));
    }
  }, [endValue, startPhase1Value, setValue]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 800 }}>
        <ModalClose />
        <Typography level={'h4'}>Neue Therapie</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            createTherapyMutation.mutate({
              id: patientId,
              data: {
                patientId: patientId,
                startPhase1: data.startPhase1,
                end: getStringOrUndefined(data.end),
                finished: false,
                therapyResult: TherapyDtoTherapyResult.PENDING,
                phase1Doctor: {
                  address: data.phase1DoctorAddress!,
                  contact: data.phase1DoctorContact ?? undefined,
                  from: getCurrentDate(),
                  patientId: patientId,
                  referenceType: PatientAddressDtoReferenceType.THERAPY_PHASE_1,
                },
                therapyMedication: data.therapyMedication.map((medication) => {
                  return { from: data.startPhase1, medication: medication, phase: TherapyMedicationDtoPhase.PHASE_1 };
                }),
              },
            }),
          )}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.startPhase1} sx={{ minWidth: 0 }}>
                <FormLabel>
                  <Typography>Start *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="startPhase1"
                  rules={{
                    required: true,
                  }}
                />
                {errors.startPhase1 && <FormHelperText>Therapie Start muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.end} sx={{ minWidth: 0 }}>
                <FormLabel>
                  <Typography>Abschluss</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="end"
                  rules={{
                    required: false,
                    ...(endMinDate ? { min: { value: endMinDate, message: 'range' } } : {}),
                  }}
                  min={endMinDate}
                />
                {errors.end && <FormHelperText>Abschluss muss nach dem Start liegen</FormHelperText>}
              </FormControl>
              <FormControl sx={{ minWidth: 0 }}>
                <FormLabel>
                  <Typography>Therapie seit</Typography>
                </FormLabel>
                <Input {...register('daysSinceStart', { disabled: true })} type={'number'} />
              </FormControl>
              <FormControl sx={{ minWidth: 0 }}>
                <FormLabel>
                  <Typography>Geplante Dauer</Typography>
                </FormLabel>
                <Input {...register('daysTotal', { disabled: true })} type={'number'} />
              </FormControl>
              <InstitutionContactFields
                formMethods={{ control, watch, setValue }}
                layout="fragment"
                addressField="phase1DoctorAddress"
                contactField="phase1DoctorContact"
                addressLabel="Behandelnder Arzt *"
                contactLabel="Kontakt"
                addressTypes={Object.values(AddressDtoAddressType)}
                canChangeAddressType={false}
                addressRules={{ required: true }}
                addressError={!!errors.phase1DoctorAddress}
                addressHelperText={errors.phase1DoctorAddress ? 'Arzt muss angegeben werden' : undefined}
                clearContactOnAddressChange="always"
                contactClearOptions={false}
                useAllContactsList={true}
                addressFormControlProps={{ sx: { minWidth: 0 } }}
                contactFormControlProps={{ sx: { minWidth: 0 } }}
              />
              <FormControl sx={{ gridColumn: '1 / span 2', minWidth: 0 }}>
                <FormLabel>
                  <Typography>Medikamente</Typography>
                </FormLabel>
                <Controller
                  name={'therapyMedication'}
                  control={control}
                  render={({ field: { onChange, value } }) => <SelectMedication value={value} onChange={onChange} />}
                />
              </FormControl>
            </FormGrid>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createTherapyMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
